import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import Constants from "../constants";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const AUTOPLAY_DELAY = 5000;
const TRANSITION_MS = 400;
const SWIPE_THRESHOLD = 50;

const PromotionalBanner = () => {
    const { i18n } = useTranslation();
    const { token } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const [images, setImages] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showCloseButton, setShowCloseButton] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    const [dragOffsetPx, setDragOffsetPx] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const [pendingDirection, setPendingDirection] = useState(null);

    const autoplayTimeoutRef = useRef(null);
    const sliderWrapperRef = useRef(null);
    const touchStartX = useRef(null);
    const touchStartY = useRef(null);
    const lastTouchX = useRef(null);

    const isRTL = i18n.language === "ar";
    const realImagesCount = images.length;

    const shuffleArray = (array) => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    };

    const hasValidEntity = useCallback((item) => {
        return typeof item?.entity === "string" && item.entity.trim() !== "";
    }, []);

    const clearAutoplay = useCallback(() => {
        if (autoplayTimeoutRef.current) {
            clearTimeout(autoplayTimeoutRef.current);
            autoplayTimeoutRef.current = null;
        }
    }, []);

    const getImageUrl = useCallback(
        (item) => {
            if (!item) return "";
            if (isRTL) {
                return (
                    (isMobile ? item.mobImageAr : item.webImageAr) ||
                    item.mobImageAr ||
                    item.webImageAr ||
                    item.mobImageEn ||
                    item.webImageEn ||
                    ""
                );
            }
            return (
                (isMobile ? item.mobImageEn : item.webImageEn) ||
                item.mobImageEn ||
                item.webImageEn ||
                item.mobImageAr ||
                item.webImageAr ||
                ""
            );
        },
        [isRTL, isMobile]
    );

    const getWrappedIndex = useCallback(
        (index) => {
            if (realImagesCount === 0) return 0;
            return ((index % realImagesCount) + realImagesCount) % realImagesCount;
        },
        [realImagesCount]
    );

    const visibleSlides = useMemo(() => {
        if (realImagesCount === 0) return [];
        if (realImagesCount === 1) return [images[0]];

        const prevIndex = getWrappedIndex(currentIndex - 1);
        const nextIndex = getWrappedIndex(currentIndex + 1);

        return [images[prevIndex], images[currentIndex], images[nextIndex]];
    }, [images, currentIndex, realImagesCount, getWrappedIndex]);

    const animateToDirection = useCallback(
        (direction) => {
            if (realImagesCount <= 1) return;
            setPendingDirection(direction);
            setIsAnimating(true);
            setDragOffsetPx(0);
        },
        [realImagesCount]
    );

    const resetAutoplay = useCallback(() => {
        clearAutoplay();

        if (
            !showModal ||
            !isVisible ||
            realImagesCount <= 1 ||
            isDragging ||
            isAnimating
        ) {
            return;
        }

        autoplayTimeoutRef.current = setTimeout(() => {
            animateToDirection("next");
        }, AUTOPLAY_DELAY);
    }, [clearAutoplay, showModal, isVisible, realImagesCount, isDragging, isAnimating, animateToDirection]);

    const handleManualNext = useCallback(() => animateToDirection("next"), [animateToDirection]);
    const handleManualPrev = useCallback(() => animateToDirection("prev"), [animateToDirection]);

    const goToSlide = useCallback(
        (index) => {
            if (realImagesCount <= 0 || index === currentIndex) return;
            setCurrentIndex(index);
            setDragOffsetPx(0);
            setPendingDirection(null);
            setIsAnimating(false);
        },
        [realImagesCount, currentIndex]
    );

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (!showModal || !isVisible) return;
        const originalBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = originalBodyOverflow;
        };
    }, [showModal, isVisible]);

    useEffect(() => {
        const fetchImages = async () => {
            const wasNavigatedFromLogin = location.state?.fromLogin;
            if (!isMobile && !wasNavigatedFromLogin) return;

            try {
                const response = await fetch(`${API_BASE_URL}/get-all-promotional-images`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        authorization: `Bearer ${token}`,
                    },
                });

                const result = await response.json();

                if (result.success && result.details?.length > 0) {
                    const today = dayjs();

                    const activeImages = result.details.filter((item) => {
                        if (!hasValidEntity(item)) return false;
                        if (!getImageUrl(item)) return false;
                        if (!item.endDate) return true;

                        return (
                            dayjs(item.endDate).isAfter(today) ||
                            dayjs(item.endDate).isSame(today, "day")
                        );
                    });

                    if (activeImages.length > 0) {
                        setImages(shuffleArray(activeImages));
                        setCurrentIndex(0);
                        setIsVisible(true);
                        setShowCloseButton(false);

                        setTimeout(() => setShowModal(true), 300);
                        setTimeout(() => setShowCloseButton(true), 300);
                    }
                }
            } catch (error) {
                console.error("PromotionalBanner Error:", error);
            }
        };

        if (token) fetchImages();
        return () => clearAutoplay();
    }, [token, isMobile, location.state, hasValidEntity, clearAutoplay, getImageUrl]);

    useEffect(() => {
        resetAutoplay();
        return clearAutoplay;
    }, [currentIndex, showModal, isVisible, resetAutoplay, clearAutoplay]);

    const handleTouchStart = (e) => {
        if (realImagesCount <= 1) return;

        clearAutoplay();
        const touch = e.touches[0];
        touchStartX.current = touch.clientX;
        touchStartY.current = touch.clientY;
        lastTouchX.current = touch.clientX;
        setIsDragging(true);
        setIsAnimating(false);
    };

    const handleTouchMove = (e) => {
        if (!isDragging || realImagesCount <= 1) return;

        const touch = e.touches[0];
        const diffX = touch.clientX - touchStartX.current;
        const diffY = touch.clientY - touchStartY.current;

        if (Math.abs(diffX) > Math.abs(diffY)) {
            e.preventDefault();
            setDragOffsetPx(diffX);
            lastTouchX.current = touch.clientX;
        }
    };

    const handleTouchEnd = () => {
        if (!isDragging || realImagesCount <= 1) return;

        const totalDeltaX = (lastTouchX.current || touchStartX.current) - touchStartX.current;
        setIsDragging(false);

        if (Math.abs(totalDeltaX) > SWIPE_THRESHOLD) {
            animateToDirection(totalDeltaX < 0 ? "next" : "prev");
        } else {
            setIsAnimating(false);
            setPendingDirection(null);
            setDragOffsetPx(0);
        }

        lastTouchX.current = null;
    };

    const handleTransitionEnd = () => {
        if (realImagesCount <= 1 || !pendingDirection) {
            setIsAnimating(false);
            setPendingDirection(null);
            setDragOffsetPx(0);
            return;
        }

        if (pendingDirection === "next") {
            setCurrentIndex((prev) => getWrappedIndex(prev + 1));
        } else if (pendingDirection === "prev") {
            setCurrentIndex((prev) => getWrappedIndex(prev - 1));
        }

        setIsAnimating(false);
        setPendingDirection(null);
        setDragOffsetPx(0);
    };

    const handleImageClick = (item) => {
        if (!item || isDragging) return;

        let entityToSend = item.entity.trim();

        if (entityToSend.toLowerCase() === Constants.ENTITY.VMCO.toLowerCase()) {
            entityToSend = item.isMachine
                ? Constants.CATEGORY.VMCO_MACHINES
                : Constants.CATEGORY.VMCO_CONSUMABLES;
        }

        navigate(`/catalog/${item.entity.trim()}`, {
            state: {
                entity: entityToSend,
                category: item.category,
                subCategory: item.subCategory,
                fromPromotionalBanner: true,
            },
        });

        setIsVisible(false);
    };

    const handleImageError = useCallback((failedId) => {
        setImages((prev) => {
            const filtered = prev.filter((img) => img.id !== failedId);

            if (filtered.length === 0) {
                setIsVisible(false);
                setShowModal(false);
                setCurrentIndex(0);
                return [];
            }

            setCurrentIndex((curr) => Math.min(curr, filtered.length - 1));
            return filtered;
        });
    }, []);

    const wrapperWidth = sliderWrapperRef.current?.offsetWidth || 0;

    let translatePx = 0;
    if (realImagesCount > 1) {
        translatePx = -wrapperWidth;

        if (isDragging) {
            translatePx = -wrapperWidth + dragOffsetPx;
        } else if (isAnimating && pendingDirection === "next") {
            translatePx = -2 * wrapperWidth;
        } else if (isAnimating && pendingDirection === "prev") {
            translatePx = 0;
        }
    }

    const styles = {
        overlay: {
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "#ffffff73",
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 999999,
            opacity: showModal ? 1 : 0,
            transition: "opacity 0.5s ease-in-out",
            pointerEvents: isVisible ? "auto" : "none",
        },
        modalContainer: {
            position: "relative",
            width: isMobile ? "85%" : "700px",
            borderRadius: "20px",
            transform: showModal ? "scale(1)" : "scale(0.7)",
            transition: "transform 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
        },
        sliderWrapper: {
            position: "relative",
            width: "100%",
            overflow: "hidden",
            borderRadius: "20px",
            boxShadow: "0 25px 50px -12px rgba(45, 44, 44, 0.2)",
            direction: "ltr",
            backgroundColor: "#f5f5f5",
        },
        sliderTrack: {
            display: "flex",
            width: realImagesCount > 1 ? "300%" : "100%",
            transform: realImagesCount > 1 ? `translateX(${translatePx}px)` : "translateX(0px)",
            transition:
                realImagesCount > 1 && !isDragging && isAnimating
                    ? `transform ${TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`
                    : "none",
            direction: "ltr",
        },
        slide: {
            width: realImagesCount > 1 ? "33.333333%" : "100%",
            flexShrink: 0,
        },
        image: {
            width: "100%",
            height: "auto",
            display: "block",
            cursor: "pointer",
            borderRadius: "20px",
        },
        closeBtn: {
            position: "absolute",
            top: "8px",
            right: isRTL ? "auto" : "8px",
            left: isRTL ? "8px" : "auto",
            backgroundColor: "#f4ececa1",
            border: "none",
            borderRadius: "50%",
            width: "24px",
            height: "24px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000002,
        },
        navBtn: (side) => ({
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
            left: side === "left" ? "-48px" : "auto",
            right: side === "right" ? "-48px" : "auto",
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            border: "none",
            background: "rgba(255, 255, 255, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            zIndex: 1000002,
        }),
        pagination: {
            position: "absolute",
            bottom: "10px",
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            gap: "8px",
            zIndex: 1000001,
            direction: "ltr",
        },
        dot: (active) => ({
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            backgroundColor: active ? "#fff" : "rgba(255,255,255,0.5)",
            border: "none",
            cursor: "pointer",
        }),
    };

    if (!isVisible || images.length === 0) return null;

    return (
        <div style={styles.overlay}>
            <div style={styles.modalContainer}>
                {showCloseButton && (
                    <button
                        style={styles.closeBtn}
                        onClick={() => setIsVisible(false)}
                    >
                        <CloseIcon style={{ fontSize: "16px" }} />
                    </button>
                )}

                <div style={{ position: "relative", width: "100%" }}>
                    {!isMobile && realImagesCount > 1 && (
                        <button
                            style={styles.navBtn("left")}
                            onClick={isRTL ? handleManualNext : handleManualPrev}
                        >
                            <ChevronLeftIcon />
                        </button>
                    )}

                    <div ref={sliderWrapperRef} style={styles.sliderWrapper}>
                        {realImagesCount === 1 ? (
                            <div style={{ width: "100%" }}>
                                <img
                                    src={getImageUrl(images[0])}
                                    alt=""
                                    style={styles.image}
                                    onClick={() => handleImageClick(images[0])}
                                    onError={() => handleImageError(images[0]?.id)}
                                />
                            </div>
                        ) : (
                            <div
                                style={styles.sliderTrack}
                                onTouchStart={isMobile ? handleTouchStart : undefined}
                                onTouchMove={isMobile ? handleTouchMove : undefined}
                                onTouchEnd={isMobile ? handleTouchEnd : undefined}
                                onTransitionEnd={handleTransitionEnd}
                            >
                                {visibleSlides.map((item, idx) => (
                                    <div key={`${item?.id}-${idx}`} style={styles.slide}>
                                        <img
                                            src={getImageUrl(item)}
                                            alt=""
                                            style={styles.image}
                                            onClick={() => handleImageClick(item)}
                                            onError={() => handleImageError(item?.id)}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}

                        {realImagesCount > 1 && (
                            <div style={styles.pagination}>
                                {images.map((_, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => goToSlide(idx)}
                                        style={styles.dot(currentIndex === idx)}
                                    />
                                ))}
                            </div>
                        )}
                    </div>

                    {!isMobile && realImagesCount > 1 && (
                        <button
                            style={styles.navBtn("right")}
                            onClick={isRTL ? handleManualPrev : handleManualNext}
                        >
                            <ChevronRightIcon />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PromotionalBanner;